import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, CheckSquare, Plus, Edit2, X, ChevronRight } from 'lucide-react';
import axios from 'axios';

// Import components and hooks
import ItemsList from './review/ItemsList';
import AssetsList from './review/AssetsList';
import AssetPreview from './review/AssetPreview';
import FeedbackPanel from './review/FeedbackPanel';
import AssetCreationWizard from './assets/creation/AssetCreationWizard';
import { useReviewState } from '../hooks/useReviewState';
import { API_BASE_URL } from '../types/review';

// Asset Request Modal Component
function AssetRequestModal({
  isOpen,
  onClose,
  onSubmit,
  requestType,
  setRequestType,
  requestDescription,
  setRequestDescription
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  requestType: string;
  setRequestType: (type: string) => void;
  requestDescription: string;
  setRequestDescription: (desc: string) => void;
}) {
  if (!isOpen) return null;

  const availableTypes = [
    { label: 'Image', value: 'image' },
    { label: 'Video', value: 'video' },
    { label: 'Copy', value: 'copy' },
    { label: 'Email Content', value: 'email' },
    { label: 'Social Media', value: 'social' },
    { label: 'Meta Tags', value: 'meta' },
    { label: 'FAQ', value: 'faq' },
    { label: 'Infographic', value: 'infographic' },
    { label: 'PDF Document', value: 'pdf' },
    { label: 'Audio', value: 'audio' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-96">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Request Asset</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Asset Type</label>
            <select
              value={requestType}
              onChange={(e) => setRequestType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select asset type...</option>
              {availableTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={requestDescription}
              onChange={(e) => setRequestDescription(e.target.value)}
              placeholder="Describe what you need..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={!requestType || !requestDescription}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300"
          >
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Component
function ReviewPageFinal() {
  const { code, planId } = useParams<{ code?: string; planId?: string }>();

  // Use the custom hook for all state management
  const reviewState = useReviewState(code, planId);

  const {
    content,
    loading,
    error,
    submitted,
    submitting,
    selectedPlan,
    availablePlans,
    selectedItem,
    planItems,
    reviewType,
    selectedAsset,
    selectedTab,
    feedback,
    feedbackPins,
    isPlacingPin,
    tempPin,
    pinComment,
    assetFeedback,
    showRequestModal,
    requestType,
    requestDescription,
    showAddAssetModal,
    uploadFile,
    uploadAssetType,
    uploadDescription,
    isUploading,
    wizardStep,

    // Setters
    setFeedback,
    setSelectedAsset,
    setIsPlacingPin,
    setPinComment,
    setShowRequestModal,
    setRequestType,
    setRequestDescription,
    setShowAddAssetModal,
    setUploadFile,
    setUploadAssetType,
    setUploadDescription,
    setIsUploading,
    setWizardStep,

    // Handlers
    fetchReviewData,
    handlePlanChange,
    handleItemSelect,
    handleTabChange,
    handlePlacePin,
    handleAddPin,
    handleCancelPin,
    handleAddAssetFeedback,
    handleApprove,
    handleReject,
    handleApproveAsset,
    handleRejectAsset,
    handleEditAsset,
    handleDeleteAsset,
    handleSaveAsset,
    handleApproveAll,
    handleNext
  } = reviewState;

  // Fetch data on mount
  useEffect(() => {
    fetchReviewData();
  }, [fetchReviewData]);

  // Handle asset request submission
  const handleAssetRequest = async () => {
    if (!requestType || !requestDescription) {
      alert('Please provide both asset type and description');
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/api/content/${content.id}/request-asset`, {
        asset_type: requestType,
        description: requestDescription,
        requested_by: reviewType === 'account_manager' ? 'Account Manager' : 'Client',
        item_title: selectedItem?.title || content?.title
      });

      alert('Asset request submitted successfully!');
      setShowRequestModal(false);
      setRequestType('');
      setRequestDescription('');
    } catch (error) {
      console.error('Error submitting asset request:', error);
      alert('Failed to submit asset request');
    }
  };

  // Utility functions
  const getFilteredAssets = () => {
    if (!content?.assets) return [];

    if (selectedTab === 'all') return content.assets;

    const typeMap: Record<string, string[]> = {
      'content': ['page', 'copy'],
      'images': ['image', 'infographic'],
      'video': ['video', 'video_ad'],
      'seo': ['seo'], // Removed 'meta' from here - will handle separately
      'schema': ['schema'],
      'media': ['podcast', 'song'],
      'audio': ['transcription'],
      'social': ['social'],
      'email': ['email'],
      'faq': ['faq'],
      // Digital Ads - consolidated under single 'ads' tab
      'ads': ['ads', 'ad', 'ad_creative', 'ad_banner', 'responsive_display', 'native_ad', 'html5_creative',
              'ad_copy', 'search_ad', 'shopping_ad', 'carousel', 'story_ad', 'collection_ad',
              'message_ad', 'document_ad', 'video_ad'],
    };

    const allowedTypes = typeMap[selectedTab] || [];

    return content.assets.filter((asset: any) => {
      // Special handling for 'meta' type files
      if (asset.asset_type === 'meta') {
        // Campaign JSON files go to ads tab
        if (selectedTab === 'ads' && asset.file_name?.includes('campaign')) {
          return true;
        }
        // Regular meta files go to SEO tab
        if (selectedTab === 'seo' && !asset.file_name?.includes('campaign')) {
          return true;
        }
        return false;
      }

      // Regular filtering for other types
      return allowedTypes.includes(asset.asset_type);
    });
  };

  const getAssetStatus = () => {
    if (!selectedAsset?.approvals) return 'pending';

    const approval = selectedAsset.approvals.find((a: any) => a.approver_type === reviewType);
    if (!approval) return 'pending';

    return approval.status;
  };

  const isTextAsset = (assetType: string) => {
    return ['copy', 'page', 'email', 'social', 'meta', 'faq', 'schema', 'ad_copy', 'search_ad', 'message_ad'].includes(assetType);
  };

  const isDigitalAd = () => {
    return currentContent?.type && ['google_ads', 'facebook_ads', 'linkedin_ads', 'display_ads'].includes(currentContent.type);
  };

  // Error and loading states
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading review...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Review Not Found</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Review Submitted!</h1>
          <p className="text-gray-600">Thank you for your feedback.</p>
        </div>
      </div>
    );
  }

  // Get current content (either single content or selected item from plan)
  const currentContent = selectedItem || content;
  const hasAssets = currentContent?.assets && currentContent.assets.length > 0;
  const filteredAssets = getFilteredAssets();
  const assetStatus = selectedAsset ? getAssetStatus() : 'pending';
  const isText = selectedAsset ? isTextAsset(selectedAsset.asset_type) : false;

  // Check if all assets are approved for current item
  const allAssetsApproved = () => {
    if (!currentContent?.assets || currentContent.assets.length === 0) return false;
    return currentContent.assets.every((asset: any) => {
      const approval = asset.asset_approvals?.find((a: any) => a.approver_type === reviewType);
      return approval?.status === 'approved';
    });
  };

  const isAllApproved = allAssetsApproved();

  // Handle reverting all approved assets back to review status
  const handleRevertToReview = async () => {
    if (!currentContent?.assets) return;

    const confirmRevert = window.confirm('Are you sure you want to revert all approved assets back to "In Review" status?');
    if (!confirmRevert) return;

    try {
      // For each approved asset, reject it with a specific message to revert status
      const revertPromises = currentContent.assets.map(async (asset: any) => {
        const approval = asset.asset_approvals?.find((a: any) => a.approver_type === reviewType);
        if (approval?.status === 'approved') {
          // Use the reject endpoint to change status back to pending/in review
          await axios.post(`${API_BASE_URL}/api/assets/${asset.id}/reject`, {
            approver_type: reviewType,
            approver_name: reviewType === 'account_manager' ? 'Account Manager' : 'Client',
            feedback: 'Reverted to In Review for further consideration'
          });
        }
      });

      await Promise.all(revertPromises);

      // Refresh data to show updated statuses
      await fetchReviewData();

      alert('All assets have been reverted to "In Review" status.');
    } catch (error) {
      console.error('Failed to revert assets:', error);
      alert('Failed to revert some assets. Please try again.');
    }
  };

  return (
    <>
      <div className="h-screen flex bg-gray-100 overflow-hidden">
        {/* Left Sidebar - Items List (for plans) */}
        {planItems.length > 0 && (
          <div className="w-64 bg-gray-50 border-r border-gray-200 flex-shrink-0">
            <ItemsList
              items={planItems}
              selectedItem={selectedItem}
              onItemSelect={handleItemSelect}
              availablePlans={availablePlans}
              selectedPlan={selectedPlan}
              onPlanChange={handlePlanChange}
              reviewType={reviewType}
            />
          </div>
        )}

        {/* Center Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header Bar with Approval Controls */}
          <div className="bg-white border-b border-gray-200 px-6 py-3 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-lg font-semibold text-gray-900">
                    {selectedItem?.title || content?.title || 'Content Review'}
                  </h1>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {reviewType === 'account_manager' ? 'Account Manager' : 'Client'} Review
                    {selectedItem && ` • ${selectedItem.type}`}
                  </p>
                </div>

                {/* Asset Info and Status */}
                {selectedAsset && (
                  <div className="flex items-center gap-2 ml-6 pl-6 border-l border-gray-200">
                    <span className="text-sm font-medium text-gray-700">
                      {selectedAsset.file_name}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      assetStatus === 'approved' ? 'bg-green-100 text-green-700' :
                      assetStatus === 'rejected' ? 'bg-red-100 text-red-700' :
                      assetStatus === 'needs_edit' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {assetStatus.replace('_', ' ')}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Asset Actions */}
                {selectedAsset && (
                  <>
                    {/* Asset Approval Button */}
                    {assetStatus === 'pending' && (
                      <button
                        onClick={() => handleApproveAsset(selectedAsset.id)}
                        className="px-3 py-1.5 bg-green-100 text-green-700 rounded-md text-sm font-medium hover:bg-green-200 transition-colors flex items-center gap-1"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Approve
                      </button>
                    )}

                    {/* Next Button */}
                    <button
                      onClick={handleNext}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}

                {/* Approve Item/All Button */}
                <button
                  onClick={isAllApproved ? handleRevertToReview : handleApproveAll}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${
                    isAllApproved
                      ? 'bg-green-100 text-green-700 hover:bg-orange-100 hover:text-orange-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title={isAllApproved ? 'Click to revert to In Review status' : 'Approve all assets'}
                >
                  <CheckSquare className="w-4 h-4" />
                  {isAllApproved
                    ? 'Approved (Click to Revert)'
                    : (planItems.length > 0 ? 'Approve Item' : 'Approve All')
                  }
                </button>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex overflow-hidden">
            {/* Assets Sidebar */}
            <div className="w-64 bg-white border-r border-gray-200 flex-shrink-0">
              <AssetsList
                title={currentContent?.title || 'Assets'}
                assets={filteredAssets}
                selectedAsset={selectedAsset}
                onAssetSelect={setSelectedAsset}
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
                onAddAsset={() => setShowAddAssetModal(true)}
                onRequestAsset={() => setShowRequestModal(true)}
                reviewerType={reviewType}
                onApproveAsset={handleApproveAsset}
                onRejectAsset={handleRejectAsset}
                onEditAsset={handleEditAsset}
                onDeleteAsset={handleDeleteAsset}
                onApproveAll={handleApproveAll}
                onAddAssetFeedback={handleAddAssetFeedback}
                assetFeedback={assetFeedback}
              />
            </div>

            {/* Main Content */}
            <div className="flex-1 bg-white overflow-hidden">
              {selectedAsset ? (
                <AssetPreview
                  selectedAsset={selectedAsset}
                  onPlacePin={handlePlacePin}
                  feedbackPins={feedbackPins[selectedAsset.id] || []}
                  isPlacingPin={isPlacingPin}
                  tempPin={tempPin}
                  apiBaseUrl={API_BASE_URL}
                  reviewType={reviewType}
                  onApproveAsset={handleApproveAsset}
                  onRejectAsset={handleRejectAsset}
                  onEditAsset={handleEditAsset}
                  onSaveAsset={handleSaveAsset}
                  onApproveAll={handleApproveAll}
                  setPinComment={setPinComment}
                  pinComment={pinComment}
                  onAddPin={handleAddPin}
                  onCancelPin={handleCancelPin}
                  contentType={currentContent?.type}
                  contentMetadata={currentContent?.metadata}
                  allAssets={currentContent?.assets || []}
                />
              ) : hasAssets ? (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <CheckSquare className="w-12 h-12 mx-auto mb-4" />
                    <p className="text-lg font-medium">Select an asset to preview</p>
                    <p className="text-sm">Choose an asset from the sidebar to view and review it.</p>
                  </div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <Plus className="w-12 h-12 mx-auto mb-4" />
                    <p className="text-lg font-medium">No assets available</p>
                    <p className="text-sm mb-4">This content doesn't have any assets yet.</p>
                    <button
                      onClick={() => setShowAddAssetModal(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add Asset
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Sidebar - Feedback Panel */}
            <div className="w-80 bg-gray-50 border-l border-gray-200 flex-shrink-0">
              <FeedbackPanel
                feedback={feedback}
                setFeedback={setFeedback}
                feedbackPins={feedbackPins[selectedAsset?.id] || []}
                isPlacingPin={isPlacingPin}
                onTogglePlacePin={() => setIsPlacingPin(!isPlacingPin)}
                tempPin={tempPin}
                pinComment={pinComment}
                onPinCommentChange={setPinComment}
                onAddPin={handleAddPin}
                onCancelPin={handleCancelPin}
                selectedAsset={selectedAsset}
                selectedItem={selectedItem}
                assetFeedback={assetFeedback}
                onAddAssetFeedback={handleAddAssetFeedback}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Asset Creation Wizard */}
      <AssetCreationWizard
        isOpen={showAddAssetModal}
        onClose={() => setShowAddAssetModal(false)}
        contentId={currentContent?.id || 0}
        onSuccess={fetchReviewData}
        apiBaseUrl={API_BASE_URL}
        clientId={currentContent?.client_id}
      />

      {/* Asset Request Modal */}
      <AssetRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        onSubmit={handleAssetRequest}
        requestType={requestType}
        setRequestType={setRequestType}
        requestDescription={requestDescription}
        setRequestDescription={setRequestDescription}
      />
    </>
  );
}

export default ReviewPageFinal;